import { useQuery } from '@tanstack/react-query';
import { fetchJson } from '../../../helpers';
import { useSelector, useDispatch } from 'react-redux';
import { useEffect } from 'react';
import { useSetWidgetAttribute } from './useSetWidgetAttribute';
import { JSONtoArr } from '../../datagrid/hooks/useServerSideData';
import { refreshSingleDatasource } from '../redux/datasourceSlice.js';
import useDatasourceWidgetChannel from './useDatasourceWidgetChannel.js';
import { selectUsesDatasourceV2 } from '../datasource_v2/selectors.js';
import {
  dashboard_get_datasource_data_path
} from '~/routes';

export const useDatasource = ({widgetId, initialDatasource, opts = {}, live = false}) => {
    const dispatch = useDispatch();
    const setAttribute = useSetWidgetAttribute(widgetId);
    const datasource = useSelector(state => state.dashboard.widgets[widgetId]?.data_datasource) || {};
    const datasourceId = useSelector(state => state.dashboard.widgets[widgetId]?.data_datasource?.id);
    const datasources = useSelector(state => state.dashboard.widgets[widgetId]?.settings?.datasources) || [];
    const { config, output_type } = initialDatasource || datasource || {};
    const {flat, table} = config || {}
    const dashboardId = useSelector(state => state.dashboard.dashboardId);
    const projectId = useSelector(state => state.dashboard.projectId);
    const disableRefresh = useSelector(state => state.dashboard.disableRefresh);
    // v2 widgets query their own datasource (see datasource_v2/)
    const usesDatasourceV2 = useSelector(selectUsesDatasourceV2(widgetId));

    let body = {live: live};
    if (live){
        body = {
            ...body,
            flat: {
                report_variables: {
                    ids: flat?.reportVariables || [],
                    opts: opts.reportVariables || {}
                },
                datasets: {
                    uuids: flat?.datasets || [],
                    opts: opts.datasets || {}
                },
                datagrid_presets: {
                    ids: flat?.datagridPresets || []
                },
                analytic_views: {
                    ids: flat?.analyticViews || []
                },
                analytic_view_presets: {
                    ids: flat?.analyticViewPresets || []
                },
            },
            output_type: output_type,
            table: {
                dataset: {
                    uuid: table?.dataset,
                    opts: opts.dataset || {}
                },
                indicator: {
                    id: table?.indicator
                },
                datagrid_preset: {
                    id: table?.datagridPreset
                },
                analytic_view: {
                    id: table?.analyticView
                },
                analytic_view_preset: {
                    id: table?.analyticViewPreset
                }
            },
        };
    }

    const {data, isLoading: loading, refetch} = useQuery({
        queryKey: ['dashboard_datasource_data', widgetId, projectId, live],
        queryFn: () => fetchJson(dashboard_get_datasource_data_path({ id: dashboardId, project_id: projectId, widget_id: widgetId, _options: true }),{ method: 'POST', body: JSON.stringify(body)}),
        enabled: !disableRefresh && !usesDatasourceV2 &&
            ((output_type === 'flat' && (flat.reportVariables || flat.datasets || flat.datagridPresets) != null) || 
            (output_type === 'table' && (table.dataset || table.datagridPreset || table.indicator) != null))
    });

    const getNumberUnit = (datasourceIndex) => (datasources[datasourceIndex] || {}).number_unit;
    const getNumberPrecision = (datasourceIndex) => (datasources[datasourceIndex] || {}).number_precision;

    const getCommonUnit = () => {
        const datasourceNumberUnits = [...new Set(datasources.map((d) => d.number_unit))];
        return datasourceNumberUnits.length == 1 ? datasourceNumberUnits[0] : null;
    }

    const getCommonPrecision = () => {
        const precision = datasources.map((d) => d.number_precision).sort()[0];
        return precision === undefined ? 2 : precision;
    }

    const getCommonNumberUnit = () => {
        const datasourceNumberUnits = [...new Set(datasources.map((d) => d.number_unit))];
        return datasourceNumberUnits.length == 1 ? datasourceNumberUnits[0] : null;
    }

    useEffect(() => {
        if (live){
            if (data?.rows){
                const rows = JSONtoArr(data.rows);
                if (datasourceId) {
                    // Write to centralized datasource store
                    dispatch(refreshSingleDatasource({ datasourceId, rows }));
                } else {
                    // New datasource not yet saved — keep legacy path until save resolves
                    setAttribute('data_datasource.rows', rows);
                }
            }
            if (data?.columnDefs){
                const headerNames = (datasource.cols || []).reduce((acc, c) => {
                    acc[c.accessor] = c.name;
                    return acc;
                }, {});
                const cols = data.columnDefs.map((c) => {
                    if (headerNames[c.accessor]) {
                        return { ...c, name: headerNames[c.accessor] };
                    }
                    return c;
                });
                if (datasourceId) {
                    dispatch(refreshSingleDatasource({ datasourceId, cols }));
                } else {
                    setAttribute('data_datasource.cols', cols);
                }
            }
        }
    }, [data, datasourceId])

    // Listen to channel updates so that new datasource data is populated upon user refreshes.
    // Requires useDatasource({ widgetId, live: true }) call in the datasource widget.
    useDatasourceWidgetChannel(usesDatasourceV2 ? null : datasourceId, projectId, refetch);

    return {
        data,
        loading,
        refetch,
        getNumberUnit,
        getNumberPrecision,
        getCommonUnit,
        getCommonPrecision,
        getCommonNumberUnit,

    }
}
